import ApiClientOpen from '../api/axios/apiClientOpen';
import type { FamiliaProductoListResponse, FamiliaProductoResponse, FamiliaProductoRequest } from '../../types';
import qs from 'qs';
import type { StrapiFilters, StrapiSort, StrapiPopulate, StrapiPagination } from '../../types/_strapi.model';

const FAMILIA_PRODUCTO_ENDPOINT = '/familia-productos';

interface GetAllFamiliaProductosParams {
  filters?: StrapiFilters;
  sort?: StrapiSort | string[];
  fields?: string[];
  populate?: StrapiPopulate | string[];
  pagination?: StrapiPagination;
}

interface GetAllParams {
  page: number;
  limit: number;
  filter?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Service class for FamiliaProducto entity operations
 */
class FamiliaProductoService {
  /**
   * Get all familia-productos with optional filtering, sorting, population, and pagination
   * @param params Object containing filters, sort, fields, populate, and pagination options
   * @returns Promise with list of familia-productos and metadata
   */
  async getAllFamiliaProductos(params?: GetAllFamiliaProductosParams): Promise<FamiliaProductoListResponse> {
    try {
      // Build query parameters using qs
      const queryParams = qs.stringify(params || {}, {
        encodeValuesOnly: true, // prettify URL
      });

      const queryString = queryParams ? `?${queryParams}` : '';
      const response = await ApiClientOpen.get<FamiliaProductoListResponse>(`${FAMILIA_PRODUCTO_ENDPOINT}${queryString}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching familia-productos:', error);
      throw error;
    }
  }

  /** 
   * Get all familia-productos with pagination, filtering, and sorting
   * @param params Object containing pagination, filtering, and sorting options
   * @returns Promise with list of familia-productos and metadata
   */
  async getAll(params: GetAllParams): Promise<FamiliaProductoListResponse> {
    try {
      const { page, limit, filter, sortBy, sortOrder } = params;
      
      // Build Strapi query parameters
      const strapiParams: any = {
        pagination: {
          page,
          pageSize: limit
        }
      };
      
      // Add filter if provided
      if (filter) {
        strapiParams.filters = {
          $or: [
            { nombre: { $contains: filter } },
            { descripcion: { $contains: filter } }
          ]
        }
      }
      
      // Add sorting if provided
      if (sortBy && sortOrder) {
        strapiParams.sort = `${sortBy}:${sortOrder}`;
      }
      
      // Build query parameters using qs
      const queryParams = qs.stringify(strapiParams, {
        encodeValuesOnly: true, // prettify URL
      });

      const queryString = queryParams ? `?${queryParams}` : '';
      const response = await ApiClientOpen.get<FamiliaProductoListResponse>(`${FAMILIA_PRODUCTO_ENDPOINT}${queryString}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching familia-productos:', error);
      throw error;
    }
  }

  /**
   * Get a familia-producto by ID with optional population and field selection
   * @param id FamiliaProducto ID
   * @param populate Optional fields to populate
   * @param fields Optional fields to select
   * @returns Promise with familia-producto data
   */
  async getFamiliaProductoById(
    id: number | string, 
    populate?: StrapiPopulate | string[], 
    fields?: string[]
  ): Promise<FamiliaProductoResponse> {
    try {
      const queryOptions: any = {};
      
      if (populate) {
        queryOptions.populate = populate;
      }
      
      if (fields) {
        queryOptions.fields = fields;
      }
      
      const queryParams = qs.stringify(queryOptions, {
        encodeValuesOnly: true,
      });
      
      const queryString = queryParams ? `?${queryParams}` : '';
      const response = await ApiClientOpen.get<FamiliaProductoResponse>(`${FAMILIA_PRODUCTO_ENDPOINT}/${id}${queryString}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching familia-producto with id ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new familia-producto
   * @param familiaProductoData FamiliaProducto data to create
   * @returns Promise with created familia-producto data
   */
  async createFamiliaProducto(familiaProductoData: FamiliaProductoRequest): Promise<FamiliaProductoResponse> {
    try {
      const response = await ApiClientOpen.post<FamiliaProductoResponse>(FAMILIA_PRODUCTO_ENDPOINT, familiaProductoData);
      return response.data;
    } catch (error) {
      console.error('Error creating familia-producto:', error);
      throw error;
    }
  }

  /**
   * Create a new familia-producto (alias method)
   * @param familiaProductoData FamiliaProducto data to create
   * @returns Promise with created familia-producto data
   */
  async create(familiaProductoData: FamiliaProductoRequest): Promise<FamiliaProductoResponse> {
    return this.createFamiliaProducto(familiaProductoData);
  }

  /**
   * Update an existing familia-producto
   * @param id FamiliaProducto ID to update
   * @param familiaProductoData Updated familia-producto data
   * @returns Promise with updated familia-producto data
   */
  async updateFamiliaProducto(id: number | string, familiaProductoData: FamiliaProductoRequest): Promise<FamiliaProductoResponse> {
    try {
      const response = await ApiClientOpen.put<FamiliaProductoResponse>(`${FAMILIA_PRODUCTO_ENDPOINT}/${id}`, familiaProductoData);
      return response.data;
    } catch (error) {
      console.error(`Error updating familia-producto with id ${id}:`, error);
      throw error;
    }
  }

  /**
   * Update an existing familia-producto (alias method)
   * @param id FamiliaProducto ID to update
   * @param familiaProductoData Updated familia-producto data
   * @returns Promise with updated familia-producto data
   */
  async update(id: number | string, familiaProductoData: FamiliaProductoRequest): Promise<FamiliaProductoResponse> {
    return this.updateFamiliaProducto(id, familiaProductoData);
  }

  /**
   * Delete a familia-producto by ID (alias method)
   * @param id FamiliaProducto ID to delete
   * @returns Promise indicating success or failure
   */
  async delete(id: number | string): Promise<void> {
    return this.deleteFamiliaProducto(id);
  }

  /**
   * Delete a familia-producto by ID
   * @param id FamiliaProducto ID to delete
   * @returns Promise indicating success or failure
   */
  async deleteFamiliaProducto(id: number | string): Promise<void> {
    try {
      await ApiClientOpen.delete(`${FAMILIA_PRODUCTO_ENDPOINT}/${id}`);
    } catch (error) {
      console.error(`Error deleting familia-producto with id ${id}:`, error);
      throw error;
    }
  }
}

export default new FamiliaProductoService();